<template>
  <v-app>
    <NavComponent></NavComponent>
    <v-main fluid class="primary fill-height">
      <router-view />
    </v-main>
    <FooterComponent v-if="showFooter"></FooterComponent>
    <Snackbar />
    <CookieConsent />
  </v-app>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import NavComponent from '@/components/Nav.vue'
import FooterComponent from '@/components/Footer.vue'
import Snackbar from '@/components/Snackbar.vue'
import CookieConsent from '@/components/CookieConsent.vue'
import { sleep } from './utils/general'
import { setPostAuthRedirect } from '@/utils/authRedirect'
import { debugLog } from '@/utils/debug'
import { getDashboardTabFromPath } from '@/router/dashboardTabs'


const redirectOff = ['/', '/sign-in', '/register', '/request', '/oneshot', '/privacy', '/terms', '/terms-glossary', '/contact', '/reset-password', '/forgot-password']


@Options({
  computed: {
    ...mapGetters(['authenticated', 'selectedTab'])
  },
  watch: {
    async authenticated(value: boolean | null) {
      if (value) {
        this.handlingAuthExpired = false
      }
      if (this.ready) {
        return
      }
      if (this.$route.path.includes('/forgot-password') || this.$route.path.includes('/reset-password')) {
        return
      }
      debugLog(this.$route.path)
      if (!value && !redirectOff.includes(this.$route.path)) {
        setPostAuthRedirect({
          fullPath: this.$route.fullPath,
          path: this.$route.path,
          selectedTab: this.getSelectedTabForPath(this.$route.path),
          source: 'app'
        })
        debugLog('redirect to sign in.', this.$route.path)
        this.$router.push('/sign-in')
      }
      await sleep(150)
      this.ready = true
    }
  },
  components: {
    NavComponent,
    FooterComponent,
    Snackbar,
    CookieConsent
  },
})
export default class App extends Vue {
  authenticated!: boolean | null
  selectedTab!: string | null
  ready: boolean = false
  private handlingAuthExpired: boolean = false
  private authExpiredHandler?: (event: Event) => void
  // route = useRoute()
  // router = useRouter()

  async mounted() {
    if (typeof window !== 'undefined') {
      this.authExpiredHandler = (event: Event) => this.onAuthExpired(event)
      window.addEventListener('auth-expired', this.authExpiredHandler)
    }
    await this.$store.dispatch('checkAuth')
    if (this.$store.getters.authenticated) {
      await this.$store.dispatch('subscription/ensureLoaded')
    }
    await sleep(500)
    this.checkPath()
    // try one more time just in case, slow connection maybe
    await sleep(1000)
    this.checkPath()
  }

  beforeUnmount() {
    if (typeof window !== 'undefined' && this.authExpiredHandler) {
      window.removeEventListener('auth-expired', this.authExpiredHandler)
    }
  }

  private getSelectedTabForPath(path: string): string | undefined {
    return getDashboardTabFromPath(path)
  }

  get showFooter(): boolean {
    const path = this.$route?.path ?? ''
    const isPublicAgentChat = path.startsWith('/agent/')
    const dashboardTab = getDashboardTabFromPath(path) ?? this.selectedTab
    const isDashboardChat = path.startsWith('/dashboard') && dashboardTab === 'chat'
    const isDashboardWorkflows = path.startsWith('/dashboard') && dashboardTab === 'workflows'
    return !isPublicAgentChat && !isDashboardChat && !isDashboardWorkflows
  }

  private onAuthExpired(_event: Event): void {
    if (!this.$route) {
      return
    }
    if (this.handlingAuthExpired) {
      return
    }
    if (this.$route.path.includes('/forgot-password') || this.$route.path.includes('/reset-password')) {
      return
    }
    if (redirectOff.includes(this.$route.path)) {
      return
    }
    this.handlingAuthExpired = true
    setPostAuthRedirect({
      fullPath: this.$route.fullPath,
      path: this.$route.path,
      selectedTab: this.getSelectedTabForPath(this.$route.path),
      source: '401'
    })
    this.$store.commit('setAuthenticated', false)
    this.$store.commit('setCanPublishAgents', false)
    this.$router.push('/sign-in')
  }

  checkPath() {
    if (this.ready) {
      return
    }
    if (this.$route.path.includes('/forgot-password') || this.$route.path.includes('/reset-password')) {
      return
    }
    if (redirectOff.includes(this.$route.path) && !this.authenticated) {
      this.ready = true
      debugLog('do not redirect to sign in', this.$route.path)
    } else if (!redirectOff.includes(this.$route.path) && !this.authenticated)  {
      setPostAuthRedirect({
        fullPath: this.$route.fullPath,
        path: this.$route.path,
        selectedTab: this.getSelectedTabForPath(this.$route.path),
        source: 'app'
      })
      debugLog('redirect to sign in', this.$route.path)
      this.ready = true
      this.$router.push('/sign-in')
    }
  }
}
  
</script>

<style lang="scss">
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  // text-align: center;
  // color: #2c3e50;
  // height: 100%;
}

.align-right {
  text-align: right;
}

.max-height-75px {
  max-height: 75px;
}

.max-width-100 {
  max-width: 100%;
}
.max-width-1250 {
  max-width: 1250px;
  margin-left: auto;
  margin-right: auto;
}

// .full-height {
//   height: 100%;
// }
a:-webkit-any-link {
  color: rgb(var(--v-theme-primary));
  &:visited {
    color: rgb(var(--v-theme-primary));
  }
}

.hide-scrollbar {
  textarea {
    overflow-y: auto !important;
    scrollbar-width: none !important;
    &::-webkit-scrollbar {
      display: none;
    }
  } 
}

.card-action-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.center-text {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.global-tooltip-icon {
  position: absolute !important;
}

.v-tooltip >.v-overlay__content {
  background: rgba(var(--v-theme-tooltip-background),0.95) !important;
  border: 1px solid rgb(var(--v-theme-border-color)) !important;
  font-size: 12px !important;
  font-weight: bold !important;
  color: rgb(var(--v-theme-tooltip-text)) !important;
}

.v-btn.v-btn--variant-plain {
  opacity: 1;

  &:hover {
    opacity: 0.8;
  }
}
// .v-card.v-card--variant-elevated {
//   background-color: rgb(var(--v-theme-background)) !important;
// }
.card-content {
  background-color: rgb(var(--v-theme-background)) !important;
}
</style>
